import {
  Component,
  type ComponentType,
  lazy,
  type ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { eventsForResponse } from "./analytics.ts";
import { type Api, createApi, defaultBaseUrl } from "./api.ts";
import { createRequestGate } from "./asyncGuards.ts";
import {
  type AuthMode,
  authMode,
  currentToken,
  loadPicks,
  type Picks,
  safeGet,
  safeSet,
  savePicks,
  signOut,
  supabaseClient,
} from "./auth.ts";
import { BRAND } from "./brand.ts";
import { CollabProvider } from "./collab/CollabProvider.tsx";
import { useWhereLabel } from "./collab/useWhereLabel.ts";
import { Login } from "./components/Login.tsx";
import { sharedConsent } from "./consent.ts";
import { captureUnsubscribeToken, UNSUBSCRIBE_HASH } from "./dailies-unsubscribe.ts";
import { connectQueryOfHash, pendingConnect } from "./data/connect.ts";
import { captureHiggsfieldReturn, completeCapturedHiggsfield, higgsfieldApi } from "./data/higgsfield-api.ts";
import { loadLegal } from "./data/legal.ts";
import { loadPricing } from "./data/money.ts";
import { applyOrder } from "./data/order.ts";
import { forgetSummaries } from "./data/production.ts";
import { loadFolded, loadRecent, pushRecent, saveFolded, saveRecent, toggleFolded } from "./data/recent.ts";
import { SourcesProvider } from "./data/SourcesProvider.tsx";
import { useSources } from "./data/sources.ts";
import { rememberWorkspace, startReads } from "./data/startReads.ts";
import { announceCreditsChange, startsPaidWork, useBalance } from "./data/useBalance.ts";
import { usePlanInfo } from "./data/usePlan.ts";
import { useProductionStates } from "./data/useSteps.ts";
import { docsPathFor, docsUrl } from "./docs.ts";
import { createApiFolderStore } from "./folders-api.ts";
import { captureInviteFromHash, pendingInvite } from "./invite.ts";
import { atLeast, isFilm } from "./lib.ts";
import { currentLocale } from "./locale.ts";
import { formatRoute, hashOfPath, legacyTarget, parseRoute, type Route, replaceRoute } from "./route.ts";
import { episodeShots } from "./scenes.ts";
import type { Project, Workspace } from "./types.ts";
import {
  AppContext,
  type AppCtx,
  errText,
  explainError,
  isTyping,
  useApp,
  useFolderList,
  useMediaQuery,
} from "./ui/app.ts";
import type { ViewMode } from "./ui/Browser.tsx";
import { ConsentBanner } from "./ui/ConsentBanner.tsx";
import { DailiesUnsubscribe } from "./ui/DailiesUnsubscribe.tsx";
import { FeatureTabs } from "./ui/FeatureNav.tsx";
import { FirstWorkspace } from "./ui/FirstWorkspace.tsx";
import { ErrorState } from "./ui/Home.tsx";
import { InviteAccept } from "./ui/InviteAccept.tsx";
import { Icon, Mark } from "./ui/icons.tsx";
import { CreditsMeter } from "./ui/kit.tsx";
import { LegalGate } from "./ui/legal/LegalGate.tsx";
import { Splash } from "./ui/MarkLoader.tsx";
import { MAKER_COMMAND_IDS, makerCommands } from "./ui/makers/commands.ts";
import { Palette, type PaletteEntry, paletteEntries } from "./ui/Palette.tsx";
import {
  BottomTabs,
  type Crumb,
  type Me,
  type NavId,
  Sidebar,
  SideSlot,
  TopBar,
  TopSlot,
} from "./ui/Shell.tsx";
import { HomeScreen } from "./ui/screens/Home.tsx";
import { forgetKeptProjects, type ProjectData, useProject } from "./useProject.ts";

// React Flow is heavy: load the board only when a canvas is opened.

const CanvasPage = lazyScreen(() => import("./canvas/CanvasPage.tsx").then((m) => m.CanvasPage), <Splash />);

/** A screen in its own chunk, so the first page does not download all the others. The bar around it stays up. */
function lazyScreen<P extends object>(
  load: () => Promise<ComponentType<P>>,
  waiting: ReactNode = null,
): ComponentType<P> {
  const Screen = lazy(() => load().then((c) => ({ default: c })));
  // biome-ignore lint/correctness/noComponentHookFactories: called once per screen at module level, never while rendering
  return function LazyScreen(props: P) {
    return (
      <ScreenBoundary>
        <Suspense fallback={waiting}>
          <Screen {...props} />
        </Suspense>
      </ScreenBoundary>
    );
  };
}

/** One screen failing (its chunk would not load) leaves the bar and the rest of the app standing. */
class ScreenBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override render() {
    return this.state.failed ? (
      <ErrorState
        title="This screen did not load"
        detail="Reload the page to try again."
        onRetry={() => window.location.reload()}
      />
    ) : (
      this.props.children
    );
  }
}

const ClaudeSettingsPage = lazyScreen(() =>
  import("./ui/connect/ClaudeSettings.tsx").then((m) => m.ClaudeSettingsPage),
);
const ConnectPage = lazyScreen(() => import("./ui/connect/ConnectPage.tsx").then((m) => m.ConnectPage));
const ImportModal = lazyScreen(() => import("./ui/import/ImportModal.tsx").then((m) => m.ImportModal));
const MakerHome = lazyScreen(() => import("./ui/makers/MakerHome.tsx").then((m) => m.MakerHome));
const ProjectView = lazyScreen(() => import("./ui/ProjectView.tsx").then((m) => m.ProjectView));
const BookScreen = lazyScreen(() => import("./ui/screens/Book.tsx").then((m) => m.BookScreen));
const CreateScreen = lazyScreen(() => import("./ui/screens/Create.tsx").then((m) => m.CreateScreen));
const CreditsScreen = lazyScreen(() => import("./ui/screens/Credits.tsx").then((m) => m.CreditsScreen));
const ElementsScreen = lazyScreen(() => import("./ui/screens/Elements.tsx").then((m) => m.ElementsScreen));
const ExploreScreen = lazyScreen(() => import("./ui/screens/Explore.tsx").then((m) => m.ExploreScreen));
const LearnScreen = lazyScreen(() => import("./ui/screens/Learn.tsx").then((m) => m.LearnScreen));
const NewProductionScreen = lazyScreen(() =>
  import("./ui/screens/NewProduction.tsx").then((m) => m.NewProductionScreen),
);
const ProductionsScreen = lazyScreen(() =>
  import("./ui/screens/Productions.tsx").then((m) => m.ProductionsScreen),
);
const SignoffScreen = lazyScreen(() => import("./ui/screens/Signoff.tsx").then((m) => m.SignoffScreen));
const WorkspaceElementScreen = lazyScreen(() =>
  import("./ui/screens/WorkspaceElement.tsx").then((m) => m.WorkspaceElementScreen),
);
const ConnectionsPage = lazyScreen(() =>
  import("./ui/settings/Connections.tsx").then((m) => m.ConnectionsPage),
);
const DailiesPage = lazyScreen(() => import("./ui/settings/Dailies.tsx").then((m) => m.DailiesPage));
const MembersPage = lazyScreen(() => import("./ui/settings/Members.tsx").then((m) => m.MembersPage));
const SharePage = lazyScreen(() => import("./ui/share/SharePage.tsx").then((m) => m.SharePage));

/** What people open next from anywhere, fetched while the browser is idle. */
function preloadScreens(): void {
  const load = () => {
    void import("./ui/ProjectView.tsx");
    void import("./ui/screens/Productions.tsx");
  };
  if ("requestIdleCallback" in window) window.requestIdleCallback(load);
  else setTimeout(load, 1000);
}

const VIEW_KEY = "filmsquare.view";

function useRoute(): [Route, (r: Route) => void] {
  const [route, setRoute] = useState<Route>(() => {
    // the import modal's link from Claude is a path (/import/<plan>): it becomes the hash address in place
    const fromPath = hashOfPath(window.location.pathname);
    if (fromPath)
      window.history.replaceState(window.history.state, "", `/${window.location.search}${fromPath}`);
    captureInviteFromHash(); // an invitation token leaves the address bar before anything renders
    captureHiggsfieldReturn(); // callback outcome/state is kept for authenticated completion, then the URL is tidied
    return parseRoute(window.location.hash);
  });
  useEffect(() => {
    const h = () => {
      captureInviteFromHash();
      setRoute(parseRoute(window.location.hash));
    };
    window.addEventListener("hashchange", h);
    return () => window.removeEventListener("hashchange", h);
  }, []);
  const go = useCallback((r: Route) => {
    const h = formatRoute(r);
    if (window.location.hash !== h) window.location.hash = h;
  }, []);
  return [route, go];
}

/** The token of a review link in the address (#/s/<token>), or null anywhere else. */
function shareTokenOfHash(): string | null {
  const r = parseRoute(window.location.hash);
  return r.view === "share" ? r.token : null;
}

function useShareToken(): string | null {
  const [token, setToken] = useState(shareTokenOfHash);
  useEffect(() => {
    const h = () => setToken(shareTokenOfHash());
    window.addEventListener("hashchange", h);
    return () => window.removeEventListener("hashchange", h);
  }, []);
  return token;
}

export function App() {
  // the unsubscribe link of a dailies mail stands outside the studio: its token is the credential, no sign-in
  const [unsub, setUnsub] = useState(() => captureUnsubscribeToken(window.location.hash));
  useEffect(() => {
    const capture = () => {
      const token = captureUnsubscribeToken(window.location.hash);
      if (token) {
        window.history.replaceState(window.history.state, "", UNSUBSCRIBE_HASH);
      }
      return token;
    };
    // If storage is blocked, a repeated effect setup must not clear the token held in state.
    const token = capture();
    if (token) setUnsub(token);
    const h = () => setUnsub(capture());
    // Capture phase also runs before an already registered analytics listener on later navigations.
    window.addEventListener("hashchange", h, true);
    sharedConsent().start();
    return () => window.removeEventListener("hashchange", h, true);
  }, []);
  return (
    <>
      {unsub ? <DailiesUnsubscribe key={unsub} token={unsub} /> : <AppRoot />}
      <ConsentBanner />
    </>
  );
}

function AppRoot() {
  const mode: AuthMode = authMode();
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  // An invitation link opened while signed out: remember it (and clear the address bar) so sign-in can carry on.
  const [invited, setInvited] = useState(() => captureInviteFromHash() || pendingInvite.get() !== null);
  useEffect(() => {
    const h = () => {
      if (captureInviteFromHash()) setInvited(true);
    };
    window.addEventListener("hashchange", h);
    return () => window.removeEventListener("hashchange", h);
  }, []);

  const shareToken = useShareToken();
  const refreshSession = useCallback(async () => setSignedIn(!!(await currentToken(mode))), [mode]);
  // signed out (after having been signed in): PostHog forgets the person on this device
  const wasSignedIn = useRef(false);
  useEffect(() => {
    if (signedIn === null) return;
    if (!signedIn && wasSignedIn.current) sharedConsent().reset();
    wasSignedIn.current = signedIn;
  }, [signedIn]);
  useEffect(() => {
    void refreshSession();
    if (mode !== "supabase") return;
    const { data } = supabaseClient().auth.onAuthStateChange(() => void refreshSession());
    return () => data.subscription.unsubscribe();
  }, [mode, refreshSession]);

  const api = useMemo(
    () =>
      createApi({
        baseUrl: defaultBaseUrl(),
        getToken: () => currentToken(mode),
        onUnauthorized: () => void signOut(mode).finally(() => setSignedIn(false)),
        // credits move when paid work starts (or is refused for lack of them): the meter looks again
        fetch: async (input, init) => {
          const res = await fetch(input, init);
          const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
          if (startsPaidWork(init?.method ?? "GET", url) && (res.ok || res.status === 402)) {
            announceCreditsChange();
          }
          const tracked = eventsForResponse(init?.method ?? "GET", url, res.status);
          if (tracked) sharedConsent().track(tracked.event, tracked.props);
          return res;
        },
      }),
    [mode],
  );

  // The server's prices (GET /v1/pricing, public) and the terms in force (GET /v1/legal, public) are in force before
  // anything shows a price or asks for the terms; if they do not answer in time the fallbacks stand.
  const [pricingReady, setPricingReady] = useState(false);
  useEffect(() => {
    let live = true;
    void Promise.all([
      loadPricing(() => api.request("GET", "/pricing")),
      loadLegal(() => api.request("GET", "/legal")),
    ]).then(() => live && setPricingReady(true));
    return () => {
      live = false;
    };
  }, [api]);

  useEffect(() => {
    if (signedIn) preloadScreens();
  }, [signedIn]);
  useEffect(() => {
    if (signedIn) startReads(api, { keySession: mode === "apikey" });
  }, [signedIn, api, mode]);
  useEffect(() => {
    if (signedIn === false) {
      forgetKeptProjects(api);
      forgetSummaries();
    }
  }, [signedIn, api]);

  // a review link: the guest page, signed in or not, outside the studio
  if (shareToken !== null) return <SharePage key={shareToken} token={shareToken} signedIn={!!signedIn} />;
  if (signedIn === null || !pricingReady) return <Splash />;
  if (!signedIn) return <SignIn mode={mode} onKey={() => void refreshSession()} invited={invited} />;
  const onSignOut = () => void signOut(mode).then(() => setSignedIn(false));
  return (
    <LegalGate api={api} apiKeySession={mode === "apikey"} onSignOut={onSignOut}>
      <Studio api={api} apiKeySession={mode === "apikey"} onSignOut={onSignOut} />
    </LegalGate>
  );
}

/**
 * The sign-in screen. A consent link from Claude (`#/connect?…`) opened while signed out is kept in this browser:
 * an email sign-in link comes back to the bare origin, and Studio returns to the consent page from there.
 */
function SignIn({ mode, onKey, invited }: { mode: AuthMode; onKey: () => void; invited: boolean }) {
  const [connecting, setConnecting] = useState(() => connectQueryOfHash(window.location.hash) !== null);
  useEffect(() => {
    const keep = () => {
      const q = connectQueryOfHash(window.location.hash);
      setConnecting(q !== null);
      if (q) pendingConnect.save(q);
    };
    keep();
    window.addEventListener("hashchange", keep);
    return () => window.removeEventListener("hashchange", keep);
  }, []);
  return <Login mode={mode} onKey={onKey} invited={invited} connecting={connecting} />;
}

function Studio({
  api,
  apiKeySession,
  onSignOut,
}: {
  api: Api;
  apiKeySession: boolean;
  onSignOut: () => void;
}) {
  const [route, go] = useRoute();
  const [picks, setPicks] = useState<Picks>(loadPicks);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [palette, setPalette] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  // the name of the workspace-wide element in view, for the crumbs (kept with its id, so it never outlives the route)
  const [elementName, setElementName] = useState<{ id: string; name: string } | null>(null);
  // where the production in view puts its folder tree: an element in the sidebar, set once it mounts
  const [sideSlot, setSideSlot] = useState<HTMLDivElement | null>(null);
  const [topSlot, setTopSlot] = useState<HTMLElement | null>(null);
  const [legacyNotice, setLegacyNotice] = useState(false);
  const [mode, setModeState] = useState<ViewMode>(() => (safeGet(VIEW_KEY) === "list" ? "list" : "grid"));
  const compact = useMediaQuery("(max-width: 760px)");

  const setMode = useCallback((m: ViewMode) => {
    setModeState(m);
    safeSet(VIEW_KEY, m);
  }, []);

  const ws = picks.ws && workspaces.some((w) => w.id === picks.ws) ? picks.ws : workspaces[0]?.id;
  const role = workspaces.find((w) => w.id === ws)?.role;
  useEffect(() => {
    if (ws) rememberWorkspace(ws);
  }, [ws]);
  // Folders belong to the workspace and are shared by the team: they live on the server.
  const folders = useMemo(() => (ws ? createApiFolderStore(api, ws) : null), [api, ws]);
  useEffect(() => {
    if (ws) void completeCapturedHiggsfield(higgsfieldApi(api, ws));
  }, [api, ws]);

  const loadGate = useRef(createRequestGate());
  const load = useCallback(
    async (prefer?: string) => {
      const current = loadGate.current.begin();
      setLoading(true);
      setProjects([]);
      setError(null);
      try {
        const list = await api.listWorkspaces();
        if (!current()) return;
        setWorkspaces(list);
        const want = prefer ?? picks.ws;
        const id = want && list.some((w) => w.id === want) ? want : list[0]?.id;
        // your own order of the productions; without it they come in creation order
        const [next, order] = id
          ? await Promise.all([api.listProjects(id), api.getProjectOrder(id).catch(() => [])])
          : [[], []];
        if (current()) setProjects(applyOrder(next, order));
      } catch (e) {
        if (current()) setError(errText(e));
      } finally {
        if (current()) setLoading(false);
      }
    },
    [api, picks.ws],
  );
  useEffect(() => {
    void load();
    return () => {
      loadGate.current.invalidate();
    };
  }, [load]);
  useEffect(() => {
    api.request<{ user?: { id?: string; email?: string; display_name?: string } }>("GET", "/me").then(
      (r) =>
        setMe({ id: r.user?.id ?? null, email: r.user?.email ?? null, name: r.user?.display_name ?? null }),
      () => setMe(null),
    );
  }, [api]);

  // analytics (only after consent) know the person by user id, never by email
  const meId = me?.id;
  useEffect(() => {
    if (meId) sharedConsent().identify(meId);
  }, [meId]);

  // The docs page of the screen in view (docs.ts): the sidebar's Docs link, the phone's account menu, "?".
  const docsHref = docsUrl(docsPathFor(route), currentLocale());
  const docsRef = useRef(docsHref);
  docsRef.current = docsHref;

  // ⌘K / Ctrl+K anywhere; "/" when not typing; "?" (not typing, no dialog open) opens the docs of this screen.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette((p) => !p);
      } else if (e.key === "/" && !isTyping(e)) {
        e.preventDefault();
        setPalette(true);
      } else if (
        e.key === "?" &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.defaultPrevented &&
        !isTyping(e) &&
        !document.querySelector("[role=dialog][aria-modal=true]")
      ) {
        e.preventDefault();
        window.open(docsRef.current, "_blank", "noopener");
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  // Remember the last project, for old addresses that do not name one.
  const lastProject = "projectId" in route ? route.projectId : null;
  useEffect(() => {
    if (lastProject) savePicks({ ...loadPicks(), project: lastProject });
  }, [lastProject]);

  // An address from the old tabbed admin (#/episode, #/elements, #/canvases) has no project: go to the one
  // last opened, or the only one there is, else to the projects list with a word of explanation.
  const legacy = route.view === "legacy" ? route.target : null;
  useEffect(() => {
    if (!legacy || loading || error) return;
    const last = loadPicks().project;
    const pid = projects.find((p) => p.id === last)?.id ?? (projects.length === 1 ? projects[0]!.id : null);
    if (pid) replaceRoute(legacyTarget(legacy, pid));
    else {
      setLegacyNotice(true);
      replaceRoute({ view: "productions" });
    }
  }, [legacy, loading, error, projects]);

  const ctx = useMemo<AppCtx | null>(
    () =>
      ws && folders
        ? {
            api,
            ws,
            role,
            folders,
            go,
            openPalette: () => setPalette(true),
            compact,
            docsHref,
            meId: me?.id ?? null,
            meName: me?.name ?? me?.email ?? null,
            onProjectChanged: (p) =>
              setProjects((l) =>
                p.archived_at ? l.filter((x) => x.id !== p.id) : l.map((x) => (x.id === p.id ? p : x)),
              ),
          }
        : null,
    [api, ws, role, folders, go, compact, me, docsHref],
  );

  // Accepted an invitation: that workspace becomes the one in view.
  const onJoined = (id: string) => {
    loadGate.current.invalidate();
    setProjects([]);
    const p = { ws: id };
    setPicks(p);
    savePicks(p);
    if (picks.ws === id) void load(id);
    replaceRoute({ view: "home" });
  };

  // Signed in with an invitation waiting (the link was opened signed out): carry on to it.
  const waiting = route.view !== "invite" && pendingInvite.get() !== null;
  useEffect(() => {
    if (waiting) replaceRoute({ view: "invite" });
  }, [waiting]);

  // Dragged in the sidebar (or moved from a row's menu): the list follows at once, the server keeps it for you.
  // Should saving fail, the list goes back to the order the server has.
  const onReorder = useCallback(
    (ids: readonly string[]) => {
      if (!ws) return;
      setProjects((l) => applyOrder(l, ids));
      api.setProjectOrder(ws, ids).catch(() =>
        api.getProjectOrder(ws).then(
          (order) => setProjects((l) => applyOrder(l, order)),
          () => undefined,
        ),
      );
    },
    [api, ws],
  );

  const onWorkspace = (id: string) => {
    loadGate.current.invalidate();
    setProjects([]);
    const p = { ws: id };
    setPicks(p);
    savePicks(p);
    go({ view: "home" });
  };

  // Signed in with a consent link waiting (it was opened signed out): go back to it.
  const resumeConnect = route.view !== "connect" && pendingConnect.has();
  useEffect(() => {
    if (!resumeConnect) return;
    const q = pendingConnect.take();
    if (q !== null) replaceRoute({ view: "connect", query: q });
  }, [resumeConnect]);

  if (route.view === "connect") {
    return (
      <ConnectPage
        key={route.query}
        api={api}
        query={route.query}
        workspaces={workspaces}
        currentWs={ws}
        loading={loading}
        loadError={error && workspaces.length === 0 ? error : null}
        email={me?.email ?? null}
        onSignOut={onSignOut}
      />
    );
  }

  if (route.view === "invite") {
    return (
      <div className="app">
        <header className="topbar">
          <span className="brand">
            <Mark />
            <span>{BRAND.name}</span>
          </span>
          <span className="grow" />
          <button type="button" className="btn ghost small" onClick={onSignOut}>
            Sign out
          </button>
        </header>
        <InviteAccept
          key={pendingInvite.version()}
          api={api}
          apiKeySession={apiKeySession}
          hasWorkspaces={workspaces.length > 0}
          onJoined={onJoined}
          onSignOut={onSignOut}
          onClose={() => replaceRoute({ view: "home" })}
        />
      </div>
    );
  }

  if (!ctx) {
    return (
      <div className="app">
        <header className="topbar">
          <span className="brand">
            <Mark />
            <span>{BRAND.name}</span>
          </span>
          <span className="grow" />
          <button type="button" className="btn ghost small" onClick={onSignOut}>
            Sign out
          </button>
        </header>
        <main className="home" id="main">
          {error ? (
            <ErrorState {...explainError(error)} onRetry={() => void load()} />
          ) : loading ? (
            <Splash label="Loading" />
          ) : (
            <FirstWorkspace api={api} onCreated={onJoined} />
          )}
        </main>
      </div>
    );
  }

  const bar = (extra: BarExtra = {}) => (
    <TopBar
      workspaces={workspaces}
      ws={ctx.ws}
      onWorkspace={onWorkspace}
      me={me}
      onSignOut={onSignOut}
      menuOpen={navOpen}
      slotRef={setTopSlot}
      {...extra}
    />
  );
  const commands: PaletteEntry[] = [
    { id: "cmd:home", group: "Go", title: "Home", icon: "home", route: { view: "home" } },
    {
      id: "cmd:productions",
      group: "Go",
      title: "All productions",
      icon: "grid",
      route: { view: "productions" },
    },
    { id: "cmd:create", group: "Go", title: "Create a shot", icon: "create", route: { view: "create" } },
    { id: "cmd:elements", group: "Go", title: "Elements", icon: "elements", route: { view: "elements" } },
    { id: "cmd:explore", group: "Go", title: "Explore", icon: "explore", route: { view: "explore" } },
    { id: "cmd:learn", group: "Go", title: "Learn", icon: "learn", route: { view: "learn" } },
    { id: "cmd:credits", group: "Go", title: "Credits and plan", icon: "dot", route: { view: "credits" } },
    { id: "cmd:new", group: "Make", title: "New production", icon: "plus", route: { view: "new" } },
    {
      id: "cmd:docs",
      group: "Go",
      title: "Docs for this screen",
      icon: "docs",
      run: () => window.open(docsHref, "_blank", "noopener"),
    },
    ...(BRAND.analytics
      ? [
          {
            id: "cmd:cookies",
            group: "Go",
            title: "Cookie settings",
            icon: "dot" as const,
            run: () => sharedConsent().reopen(),
          },
        ]
      : []),
    {
      id: "cmd:view",
      group: "Go",
      title: mode === "grid" ? "Show as list" : "Show as grid",
      icon: mode === "grid" ? "list" : "grid",
      run: () => setMode(mode === "grid" ? "list" : "grid"),
    },
  ];
  commands.push(...makerCommands({ role, go, projects, scope: null, api, ws: ctx.ws }));
  const runEntry = (e: PaletteEntry) => {
    setPalette(false);
    if (e.run) e.run();
    else if (e.route) go(e.route);
  };
  const paletteEl = palette && (
    <Palette
      entries={paletteEntries(projects, null, [])}
      commands={commands}
      onGo={runEntry}
      onClose={() => setPalette(false)}
    />
  );
  const nav = navOf(route);

  let screen: ReactNode = null;
  switch (route.view) {
    case "home":
      screen = (
        <>
          {bar({
            where: role === "maker" ? "Your work" : "Home",
            ...(role !== "maker" && projects.length > 0
              ? {
                  action: (
                    // under 480px only the plus shows, so the bar fits a phone (the name stays for screen readers)
                    <a
                      className="k-btn k-outline top-new"
                      href={formatRoute({ view: "new" })}
                      aria-label="New production"
                    >
                      <Icon name="plus" size={14} />
                      <span className="top-new-text">New production</span>
                    </a>
                  ),
                }
              : {}),
          })}
          {role === "maker" ? (
            <MakerHome
              projects={projects}
              projectsLoading={loading}
              projectsError={error}
              onRetry={() => void load()}
            />
          ) : (
            <HomeScreen
              key={ctx.ws}
              projects={projects}
              loading={loading}
              error={error}
              onRetry={() => void load()}
            />
          )}
          {paletteEl}
        </>
      );
      break;
    case "productions":
      screen = (
        <>
          {bar({ where: "Productions" })}
          <ProductionsScreen
            key={ctx.ws}
            projects={projects}
            loading={loading}
            error={error}
            onRetry={() => void load()}
            notice={
              legacyNotice
                ? "That address is from the old admin, which had no productions. Pick a production to continue."
                : null
            }
            onDismissNotice={() => setLegacyNotice(false)}
          />
          {paletteEl}
        </>
      );
      break;
    case "canvas":
      screen = (
        <>
          {bar({ where: "Canvas" })}
          <Suspense fallback={<Splash />}>
            <div className="canvas-frame">
              <CanvasPage
                key={`${ctx.ws}:${route.id}`}
                api={api}
                ws={ctx.ws}
                role={role}
                canvasId={route.id}
                projectId={route.projectId}
                backLabel="Back"
                onBack={() =>
                  window.history.length > 1
                    ? window.history.back()
                    : go(
                        route.projectId
                          ? { view: "project", projectId: route.projectId, loc: { kind: "canvases" } }
                          : { view: "home" },
                      )
                }
              />
            </div>
          </Suspense>
          {paletteEl}
        </>
      );
      break;
    case "settings":
      screen = (
        <>
          {bar({
            where:
              route.page === "claude"
                ? "Your Claude"
                : route.page === "dailies"
                  ? "Dailies"
                  : route.page === "connections"
                    ? "Connections"
                    : atLeast(role, "producer")
                      ? "Members and access"
                      : "Members",
          })}
          {route.page === "claude" ? (
            <ClaudeSettingsPage key={ctx.ws} />
          ) : route.page === "dailies" ? (
            <DailiesPage key={ctx.ws} />
          ) : route.page === "connections" ? (
            <ConnectionsPage key={ctx.ws} />
          ) : (
            <MembersPage key={ctx.ws} />
          )}
          {paletteEl}
        </>
      );
      break;
    case "legacy":
      screen = (
        <>
          {bar()}
          <main className="home" id="main">
            <Splash label="Opening" />
          </main>
        </>
      );
      break;
    case "project":
    case "shot":
    case "element":
      screen = (
        <ProjectScope
          key={`${ctx.ws}:${route.projectId}`}
          route={route}
          projects={projects}
          bar={bar}
          mode={mode}
          onMode={setMode}
          navOpen={navOpen}
          onNav={setNavOpen}
          palette={palette}
          commands={commands}
          onPaletteGo={runEntry}
          onPaletteClose={() => setPalette(false)}
        />
      );
      break;
    case "explore":
      screen = (
        <>
          {bar({ where: "Explore" })}
          <ExploreScreen />
          {paletteEl}
        </>
      );
      break;
    case "book":
      screen = (
        <>
          {bar({ crumbs: [{ label: "Explore", href: "#/explore" }, { label: "Production Book" }] })}
          <BookScreen key={route.id} id={route.id} />
          {paletteEl}
        </>
      );
      break;
    case "learn":
      screen = (
        <>
          {bar({ where: "Learn" })}
          <LearnScreen />
          {paletteEl}
        </>
      );
      break;
    case "create":
      screen = (
        <>
          {bar({ where: "Create" })}
          {/* one Create per workspace: the sketchbook, start frame, history and an unfinished batch never cross */}
          <CreateScreen key={ws} />
          {paletteEl}
        </>
      );
      break;
    case "signoff":
      screen = (
        <>
          {bar({ crumbs: [{ label: "Home", href: "#/" }, { label: "Waiting on you" }] })}
          <SignoffScreen />
          {paletteEl}
        </>
      );
      break;
    case "elements":
      screen = (
        <>
          {bar({ where: "Elements" })}
          <ElementsScreen projects={projects} />
          {paletteEl}
        </>
      );
      break;
    case "workspace-element":
      screen = (
        <>
          {bar({
            crumbs: [
              { label: "Elements", href: formatRoute({ view: "elements" }) },
              { label: elementName?.id === route.elementId ? elementName.name : "Element" },
            ],
          })}
          <WorkspaceElementScreen
            key={`${ctx.ws}:${route.elementId}`}
            elementId={route.elementId}
            onName={(name) => setElementName({ id: route.elementId, name })}
          />
          {paletteEl}
        </>
      );
      break;
    case "credits":
      screen = (
        <>
          {bar({ where: "Credits & plan" })}
          <CreditsScreen projects={projects} />
          {paletteEl}
        </>
      );
      break;
    case "import":
      // the import modal over Home: Close goes back to it
      screen = (
        <>
          {bar({
            crumbs: [{ label: "Productions", href: "#/productions" }, { label: "Import from Higgsfield" }],
          })}
          <HomeScreen
            key={ctx.ws}
            projects={projects}
            loading={loading}
            error={error}
            onRetry={() => void load()}
          />
          <ImportModal
            key={`${ctx.ws}:${route.planId}`}
            planId={route.planId}
            projects={projects}
            onProductions={() => void load()}
          />
          {paletteEl}
        </>
      );
      break;
    case "new":
      screen = (
        <>
          {bar({ crumbs: [{ label: "Productions", href: "#/productions" }, { label: "New production" }] })}
          <NewProductionScreen
            step={route.step ?? 1}
            onCreated={(p) => {
              setProjects((l) => (l.some((x) => x.id === p.id) ? l : [...l, p]));
              go({ view: "project", projectId: p.id, loc: { kind: "root" } });
            }}
          />
          {paletteEl}
        </>
      );
      break;
  }

  return (
    <SourcesProvider api={api} ws={ctx.ws} meId={me?.id}>
      <AppContext.Provider value={ctx}>
        <CollabProvider api={api} ws={ctx.ws} role={role} route={route} go={go}>
          <SideSlot.Provider value={compact ? null : sideSlot}>
            <TopSlot.Provider value={compact ? null : topSlot}>
              <div
                className={`app${compact ? " with-tabs" : ` with-side${nav === "create" ? " side-rail" : ""}`}`}
              >
                {/* biome-ignore lint/a11y/useValidAnchor: a real same-page skip link; the handler only moves focus onto #main since it has no native tabindex */}
                <a
                  className="skip"
                  href="#main"
                  onClick={(e) => {
                    const el = document.getElementById("main");
                    if (!el) return;
                    e.preventDefault();
                    if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
                    el.focus();
                  }}
                >
                  Skip to content
                </a>
                {!compact && (
                  <ShellSidebar
                    workspaces={workspaces}
                    ws={ctx.ws}
                    onWorkspace={onWorkspace}
                    me={me}
                    onSignOut={onSignOut}
                    projects={projects}
                    onReorder={onReorder}
                    nav={nav}
                    activeProject={"projectId" in route ? route.projectId : null}
                    place={route.view}
                    slotRef={setSideSlot}
                  />
                )}
                {screen}
                {compact && <BottomTabs nav={nav} />}
              </div>
            </TopSlot.Provider>
          </SideSlot.Provider>
        </CollabProvider>
      </AppContext.Provider>
    </SourcesProvider>
  );
}

type BarExtra = Partial<
  Pick<Parameters<typeof TopBar>[0], "onMenu" | "where" | "crumbs" | "tabs" | "action">
>;

/** The top bar's trail inside a production: Productions / Moneyshots / Episode 2 / Shot 3 (or Crew). */
function projectCrumbs(
  route: Extract<Route, { view: "project" | "shot" | "element" }>,
  d: ProjectData | null,
  fallbackName: string | undefined,
): Crumb[] {
  const pid = route.projectId;
  const out: Crumb[] = [
    { label: "Productions", href: "#/productions" },
    {
      label: d?.project.name ?? fallbackName ?? "Production",
      href: formatRoute({ view: "project", projectId: pid, loc: { kind: "root" } }),
    },
  ];
  const episodeCrumb = (id: string) => {
    const e = d?.episodes.find((x) => x.id === id);
    if (e)
      out.push({
        label: d && isFilm(d.project.type) ? "Film" : `Episode ${e.number}`,
        href: formatRoute({ view: "project", projectId: pid, loc: { kind: "episode", id } }),
      });
  };
  if (route.view === "shot") {
    const home = d?.shotHome.get(route.shotId);
    if (home) episodeCrumb(home.episodeId);
    const shots = home && d ? episodeShots(d, home.episodeId) : [];
    const i = shots.findIndex((s) => s.id === route.shotId);
    out.push({ label: i >= 0 ? `Shot ${i + 1}` : "Shot" });
  } else if (route.view === "element") {
    out.push({ label: d?.elements.find((e) => e.id === route.elementId)?.name ?? "Element" });
  } else if (route.loc.kind === "episode" || route.loc.kind === "scene") {
    episodeCrumb(route.loc.kind === "episode" ? route.loc.id : route.loc.episodeId);
  } else if (route.loc.kind === "cut") {
    episodeCrumb(route.loc.episodeId);
    out.push({ label: "Cut" });
  } else if (route.loc.kind === "storyboard") {
    episodeCrumb(route.loc.episodeId);
    out.push({ label: "Storyboard" });
  } else if (route.loc.kind === "tools" || route.loc.kind === "review") {
    episodeCrumb(route.loc.episodeId);
    out.push({ label: route.loc.kind === "tools" ? "Tools" : "Review" });
  } else if (route.loc.kind === "blocking") {
    episodeCrumb(route.loc.episodeId);
    out.push({ label: "3D" });
  } else if (route.loc.kind === "tags") {
    episodeCrumb(route.loc.episodeId);
    out.push({ label: "Script" });
  } else if (route.loc.kind === "crew") {
    out.push({ label: "Crew" });
  } else if (route.loc.kind === "settings") {
    out.push({ label: "Settings" });
  }
  return out;
}

/** Which of the six main places a route belongs to. */
function navOf(r: Route): NavId | null {
  switch (r.view) {
    case "home":
    case "signoff":
      return "home";
    case "create":
    case "shot":
      return "create";
    case "productions":
    case "new":
    case "import":
    case "project":
    case "canvas":
      return "productions";
    case "elements":
    case "element":
    case "workspace-element":
      return "elements";
    case "explore":
    case "book":
      return "explore";
    case "learn":
      return "learn";
    default:
      return null;
  }
}

/** The sidebar with what it reads: the Recent productions (and their steps) and the credits balance. */
function ShellSidebar(
  props: Omit<Parameters<typeof Sidebar>[0], "productions" | "states" | "meter" | "folded" | "onFold"> & {
    projects: readonly Project[];
    /** the place in view: the meter looks again when it changes */
    place: string;
  },
) {
  const { api, ws } = useApp();
  const { billing } = useSources();
  const { projects, activeProject, place, ...rest } = props;
  // Home's "Continue" starts from the production opened last
  useEffect(() => {
    if (activeProject) saveRecent(pushRecent(loadRecent(), activeProject));
  }, [activeProject]);
  const [foldedIds, setFoldedIds] = useState(loadFolded);
  const folded = useMemo(() => new Set(foldedIds), [foldedIds]);
  const onFold = useCallback((id: string) => {
    setFoldedIds((l) => {
      const next = toggleFolded(l, id);
      saveFolded(next);
      return next;
    });
  }, []);
  const states = useProductionStates(api, ws, projects);
  const balance = useBalance(billing, ws, place);
  const plan = usePlanInfo(billing, ws, place);
  return (
    <Sidebar
      {...rest}
      activeProject={activeProject}
      productions={projects}
      folded={folded}
      onFold={onFold}
      states={states}
      meter={
        balance && (
          <CreditsMeter
            metered={balance.billing === "credits"}
            credits={balance.credits}
            usedCredits={balance.usedCredits}
            usedByDept={balance.usedByDept}
            addHref="#/credits"
            addLabel={plan?.plan === "free" ? "Get seats" : undefined}
            planCredits={plan?.plan === "team" || plan?.plan === "custom" ? plan.planCreditsLeft : undefined}
            monthlyCredits={plan?.planCreditsPerMonth}
          />
        )
      }
    />
  );
}

function ProjectScope({
  route,
  projects,
  bar,
  mode,
  onMode,
  navOpen,
  onNav,
  palette,
  commands,
  onPaletteGo,
  onPaletteClose,
}: {
  route: Extract<Route, { view: "project" | "shot" | "element" }>;
  projects: Project[];
  bar: (extra: BarExtra) => ReactNode;
  mode: ViewMode;
  onMode: (m: ViewMode) => void;
  navOpen: boolean;
  onNav: (o: boolean) => void;
  palette: boolean;
  commands: PaletteEntry[];
  onPaletteGo: (e: PaletteEntry) => void;
  onPaletteClose: () => void;
}) {
  const ctx = useApp();
  const proj = useProject(ctx.api, ctx.ws, route.projectId);
  const folders = useFolderList(ctx.folders, route.projectId);
  useWhereLabel(proj.data, route, folders);
  const pid = route.projectId;
  const withFolder: PaletteEntry[] = [
    // the maker commands know which project we are in
    ...commands.filter((c) => !MAKER_COMMAND_IDS.has(c.id)),
    ...makerCommands({ role: ctx.role, go: ctx.go, projects, scope: pid, api: ctx.api, ws: ctx.ws }),
    {
      id: "cmd:root",
      group: "Go",
      title: "Production folders",
      icon: "folder",
      route: { view: "project", projectId: pid, loc: { kind: "root" } },
    },
  ];
  return (
    <>
      {bar({
        onMenu: ctx.compact ? () => onNav(!navOpen) : undefined,
        crumbs: projectCrumbs(route, proj.data, projects.find((p) => p.id === pid)?.name),
        tabs: ctx.compact ? undefined : <FeatureTabs d={proj.data} route={route} />,
      })}
      {ctx.compact ? (
        <div className="ft-phone">
          <FeatureTabs d={proj.data} route={route} />
          <ProjectView
            proj={proj}
            route={route}
            mode={mode}
            onMode={onMode}
            navOpen={navOpen}
            onNav={onNav}
          />
        </div>
      ) : (
        <ProjectView proj={proj} route={route} mode={mode} onMode={onMode} navOpen={navOpen} onNav={onNav} />
      )}
      {palette && (
        <Palette
          entries={paletteEntries(projects, proj.data, folders)}
          commands={withFolder}
          onGo={onPaletteGo}
          onClose={onPaletteClose}
        />
      )}
    </>
  );
}
